"use client";

import React from "react";

import { useRouter } from "next/navigation";

import { ProductDetailPage } from "@/components/product/ProductDetailPage";
import { getProductAnyId, type Product } from "@/lib/product-utils";

import { mergeMissingPdpFields } from "./product-page-data";
import {
  navigateBackToShopping,
  ProductBreadcrumbs,
  getProductBreadcrumbs,
  useScrollToTopOnProductNavigation,
} from "./product-page-shared";

interface ProductPageServerClientProps {
  product: Product;
  productId: string;
}

/**
 * Client boundary for the server-rendered canonical PDP path.
 *
 * Product data is fetched in the server route (`page.tsx`) and passed here as props.
 * This component keeps browser-only behavior (tracking, router actions, interactive
 * breadcrumbs) without moving data fetching back to the client.
 */
export default function ProductPageServerClient({
  product,
  productId,
}: ProductPageServerClientProps) {
  const router = useRouter();
  const [effectiveProduct, setEffectiveProduct] = React.useState<Product>(product);

  useScrollToTopOnProductNavigation(productId);

  React.useEffect(() => {
    setEffectiveProduct(product);
  }, [product]);

  React.useEffect(() => {
    try {
      const rawCached = localStorage.getItem(`product-${productId}`);
      if (!rawCached) return;

      const cachedProduct = JSON.parse(rawCached) as Product;
      if (getProductAnyId(cachedProduct) !== productId) return;

      setEffectiveProduct((currentProduct) => mergeMissingPdpFields(currentProduct, cachedProduct));

      // Keep behavior aligned with client PDP warm cache semantics.
      window.setTimeout(() => {
        try {
          localStorage.removeItem(`product-${productId}`);
        } catch {
          // localStorage can be unavailable; ignore cleanup failures.
        }
      }, 30_000);
    } catch {
      // Storage can be unavailable (private mode) or malformed; safe to ignore.
    }
  }, [productId]);

  // Navigation authority comes exclusively from this server product, never a browser cache.
  const breadcrumbPath = React.useMemo(() => getProductBreadcrumbs(product), [product]);

  const handleBackToShopping = () => {
    navigateBackToShopping(router);
  };

  return (
    <div>
      <ProductBreadcrumbs
        key={productId}
        productId={productId}
        path={breadcrumbPath}
        router={router}
      />

      <ProductDetailPage
        product={effectiveProduct}
        onBackToShopping={handleBackToShopping}
        preferEscapedDescriptionUntilHydrated
      />
    </div>
  );
}
